import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const paisesOptions = [
  'ARGENTINA', 'BRASIL', 'CHILE', 'URUGUAY', 'PARAGUAY', 'BOLIVIA', 'PERU',
  'COLOMBIA', 'VENEZUELA', 'ECUADOR', 'MEXICO', 'ESPAÑA', 'ITALIA', 'FRANCIA',
  'ALEMANIA', 'ESTADOS UNIDOS', 'CANADA', 'REINO UNIDO', 'REPUBLICA DE COREA', 'OTRO'
];

const SeccionDomicilio = ({ solicitudId, tipo }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    calle: '',
    numero: '',
    piso: '',
    depto: '',
    barrio: '',
    ciudad: '',
    provincia: '',
    pais: 'ARGENTINA',
    cp: '',
    tipo: tipo === 'individuo' ? 'LEGAL' : 'REAL'
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const getMethod = tipo === 'individuo' 
        ? solicitudDetalleService.getDomicilioIndividuo
        : solicitudDetalleService.getDomicilioEmpresa;
      const response = await getMethod(solicitudId);
      if (response) {
        setFormData({
          calle: response.calle || '',
          numero: response.numero || '',
          piso: response.piso || '',
          depto: response.depto || '',
          barrio: response.barrio || '',
          ciudad: response.ciudad || '',
          provincia: response.provincia || '',
          pais: response.pais || 'ARGENTINA',
          cp: response.cp || '',
          tipo: response.tipo || (tipo === 'individuo' ? 'LEGAL' : 'REAL')
        });
      }
    } catch (err) {
      setError('Error al cargar el domicilio');
    } finally {
      setLoading(false);
    }
  }, [solicitudId, tipo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      const saveMethod = tipo === 'individuo'
        ? solicitudDetalleService.saveDomicilioIndividuo
        : solicitudDetalleService.saveDomicilioEmpresa;
      await saveMethod(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Domicilio
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Calle"
                required
                value={formData.calle}
                onChange={(e) => handleInputChange('calle', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero}
                onChange={(e) => handleInputChange('numero', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código Postal"
                required
                value={formData.cp}
                onChange={(e) => handleInputChange('cp', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Piso"
                value={formData.piso}
                onChange={(e) => handleInputChange('piso', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Departamento"
                value={formData.depto}
                onChange={(e) => handleInputChange('depto', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>País</InputLabel>
                <Select
                  value={formData.pais}
                  onChange={(e) => handleInputChange('pais', e.target.value)}
                  label="País"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Provincia"
                required
                value={formData.provincia}
                onChange={(e) => handleInputChange('provincia', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ciudad"
                required
                value={formData.ciudad}
                onChange={(e) => handleInputChange('ciudad', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Barrio"
                value={formData.barrio}
                onChange={(e) => handleInputChange('barrio', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDomicilio;
